/**
 * What an older media_server meant by a key it left out, so a lenient parse can read its data.
 * See *A media_server far behind* in docs/rules/api_boundary.md.
 */

import { z } from 'zod';

import { entrySchemas, FieldsSchema } from './listOf';

/** The members an enum or a literal may be filled with: the ones that mean nothing set. */
const EMPTY_MEMBERS: readonly unknown[] = ['', 0];

/** What fields declare; a schema is its own key, so a field keeps it wherever it is used. */
const declaredDefaults = z.registry<{ value: unknown }>();

/** Declares what an older server meant when it left out a field of `schema`. */
export function declareMediaServerDefault<S extends z.ZodType>(schema: S, value: z.input<S>): S {
    declaredDefaults.add(schema, { value });
    return schema;
}

/** The empty value of a scalar; undefined for one that has none. */
function getEmptyScalar(schema: z.core.$ZodType): unknown {
    if (schema instanceof z.core.$ZodString) {
        return '';
    }
    if (schema instanceof z.core.$ZodNumber) {
        return 0;
    }
    if (schema instanceof z.core.$ZodBoolean) {
        return false;
    }
    if (schema instanceof z.core.$ZodLiteral || schema instanceof z.core.$ZodEnum) {
        return [...schema._zod.values].find((member) => EMPTY_MEMBERS.includes(member));
    }
    return undefined;
}

function getFilledObject(schema: z.core.$ZodObject, value: unknown): unknown {
    const fields = FieldsSchema.safeParse(value === undefined ? {} : value);
    if (!fields.success) {
        return value;
    }
    const shape = Object.entries(schema._zod.def.shape);
    const filledItems = shape.flatMap(([key, field]): [string, unknown][] => {
        const nextValue = getFilled(field, fields.data[key]);
        return nextValue === undefined ? [] : [[key, nextValue]];
    });
    return {
        ...fields.data,
        ...Object.fromEntries(filledItems),
    };
}

function getFilledEntries(
    kind: 'list' | 'record', entry: z.core.$ZodType, value: unknown,
): unknown {
    if (value === undefined) {
        return kind === 'list' ? [] : {};
    }
    if (Array.isArray(value)) {
        return value.map((item: unknown): unknown => getFilled(entry, item));
    }
    const fields = FieldsSchema.safeParse(value);
    if (!fields.success) {
        return value;
    }
    return Object.fromEntries(Object.entries(fields.data).map(
        ([key, item]): [string, unknown] => [key, getFilled(entry, item)],
    ));
}

/** The option of a discriminated union whose tag the value names; null for an unknown tag. */
function getTagOption(
    union: z.core.$ZodDiscriminatedUnion, value: unknown,
): z.core.$ZodType | null {
    const { discriminator, options } = union._zod.def;
    const fields = FieldsSchema.safeParse(value);
    const tag = fields.success ? fields.data[discriminator] : undefined;
    return options.find((option) => {
        const tags: ReadonlySet<unknown> | undefined = option._zod.propValues?.[discriminator];
        return tags?.has(tag) ?? false;
    }) ?? null;
}

/**
 * `value` with every key its schema requires and it lacks filled: with what the field declares,
 * else with the empty value of its type. A missing `value` is filled whole; undefined leaves it
 * missing. A value that is present is kept as it is, wrong or right: only a missing key is what
 * an older server can explain.
 */
export function getFilled(schema: z.core.$ZodType, value: unknown): unknown {
    const declaredValue = declaredDefaults.get(schema);
    const entries = entrySchemas.get(schema);
    if (value === undefined && declaredValue !== undefined) {
        return declaredValue.value;
    }
    if (entries !== undefined) {
        const { entry, kind } = entries;
        return entry instanceof z.core.$ZodType ? getFilledEntries(kind, entry, value) : value;
    }
    if (schema instanceof z.core.$ZodNullable) {
        return value === undefined ? null : getFilled(schema._zod.def.innerType, value);
    }
    if (
        schema instanceof z.core.$ZodOptional || schema instanceof z.core.$ZodCatch ||
        schema instanceof z.core.$ZodDefault
    ) {
        return value === undefined ? value : getFilled(schema._zod.def.innerType, value);
    }
    if (schema instanceof z.core.$ZodPipe) {
        return getFilled(schema._zod.def.in, value);
    }
    if (schema instanceof z.core.$ZodObject) {
        return getFilledObject(schema, value);
    }
    if (schema instanceof z.core.$ZodArray) {
        return getFilledEntries('list', schema._zod.def.element, value);
    }
    if (schema instanceof z.core.$ZodRecord) {
        return getFilledEntries('record', schema._zod.def.valueType, value);
    }
    if (schema instanceof z.core.$ZodDiscriminatedUnion) {
        const option = getTagOption(schema, value);
        return option === null ? value : getFilled(option, value);
    }
    return value === undefined ? getEmptyScalar(schema) : value;
}
